import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { getActiveRumViewId, getRumClient } from '../../lib/rum/rumClient';
import { isDqPrefillEnabled } from '../../lib/proctor/isDqPrefillEnabled';
import { AutoApplyRumContext } from './autoApplyRumErrors';

type Operation =
    | 'prefill'
    | 'download-preview'
    | typeof AutoApplyRumContext.ReviewPreviewCreation.operation
    | typeof AutoApplyRumContext.ReviewPreviewDecode.operation
    | typeof AutoApplyRumContext.ConfirmationSubmit.operation;
type Outcome =
    | 'success'
    | 'application-error'
    | 'network-error'
    | 'fallback'
    | 'missing-identifiers';

/** Explicit allowlist: never pass API payloads, IDs, URLs, answers or error messages here. */
export interface DiagnosticState {
    scope?: 'page' | 'preview';
    loading?: boolean;
    pending?: boolean;
    failed?: boolean;
    draftCount?: number;
    usableCount?: number;
    excludedCount?: number;
    consentRequired?: boolean;
    consentChecked?: boolean;
    submitting?: boolean;
    deleting?: boolean;
    flowLoading?: boolean;
    previewPending?: boolean;
    draftPending?: boolean;
    ctaVisible?: boolean;
    ctaDisabled?: boolean;
    previewSource?: 'cache' | 'mutation' | 'skipped' | 'undecided';
    htmlAvailable?: boolean;
    dqHtmlAvailable?: boolean;
}

/** Banner and DQ allocations shared by diagnostic actions and the RUM global context. */
export function getAutoApplyBuckets(getGroup: ReturnType<typeof useProctorGroups>['getGroup']) {
    const bannerBucket = getGroup('rnhp_auto_apply_banner_tst')?.value ?? null;
    // getGroup tracks exposure; diagnostics must preserve the product's DQ population gate.
    const dqBucket =
        bannerBucket === 2 ? (getGroup('ia_dq_questions_prefill_tst')?.value ?? null) : null;
    return { bannerBucket, dqBucket };
}

/** Capture route/allocation at the start, so a late result does not inherit another draft's view. */
export function useAutoApplyDiagnostics() {
    const { pathname, search } = useLocation();
    const { getGroup } = useProctorGroups();
    const { bannerBucket, dqBucket } = getAutoApplyBuckets(getGroup);
    const dqEnabled = isDqPrefillEnabled(getGroup);
    const ids = new URLSearchParams(search).get('draftIds')?.split(',').filter(Boolean) ?? [];
    const draftOrdinal = ids.indexOf(pathname.split('/')[2]);

    return useMemo(() => {
        const mode = pathname.endsWith('/edit') ? 'edit' : 'default';
        const pagePath = pathname.replace(/\/edit$/, '');
        const page = pagePath.endsWith('/review')
            ? AutoApplyRumContext.ReviewDraftLoad.page
            : pagePath.endsWith('/confirmation')
              ? AutoApplyRumContext.ConfirmationLoad.page
              : pagePath.endsWith('/questions')
                ? AutoApplyRumContext.QuestionsLoad.page
                : AutoApplyRumContext.DraftValidation.page;
        const base = { page, mode, bannerBucket, dqBucket, dqEnabled, draftOrdinal };
        let originViewId: string | undefined;
        try {
            originViewId = getActiveRumViewId();
        } catch {
            /* telemetry is best effort */
        }
        const capture = (draftProctors?: string) => {
            const started = Date.now();
            // Draft-creation allocations can differ from the current browser allocations.
            const draftBucket = (name: string) => {
                const token = draftProctors
                    ?.split(',')
                    .map((value) => value.trim())
                    .find((value) => new RegExp(`^${name}-?\\d+$`).test(value));
                return token ? Number(token.slice(name.length)) : null;
            };
            const draftAllocations =
                draftProctors === undefined
                    ? {}
                    : {
                          draftDqBucket: draftBucket('ia_dq_questions_prefill_tst'),
                          draftBannerBucket: draftBucket('rnhp_auto_apply_banner_tst'),
                          draftRelaxBucket: draftBucket('ia_rbs_relax_sq_requirement_tst'),
                      };
            return (
                event: 'state' | 'exit' | 'pagehide' | 'long-wait' | 'start' | 'finish',
                details: DiagnosticState & {
                    operation?: Operation;
                    operationSequence?: number;
                    outcome?: Outcome;
                    draftOrdinal?: number;
                } = {},
            ) => {
                try {
                    getRumClient()?.addAction?.('auto-apply-diagnostic', {
                        feature: 'auto-apply',
                        diagnosticVersion: 1,
                        scope: 'page',
                        ...base,
                        ...draftAllocations,
                        originViewId,
                        event,
                        elapsedMs: Date.now() - started,
                        ...details,
                    });
                } catch {
                    /* Logging must never alter navigation or request outcomes. */
                }
            };
        };
        let operationSequence = 0;
        return {
            capture,
            start(operation: Operation, ordinal = draftOrdinal, draftProctors?: string) {
                const emit = capture(draftProctors);
                const details = {
                    operation,
                    draftOrdinal: ordinal,
                    operationSequence: ++operationSequence,
                };
                emit('start', details);
                const timer = setTimeout(() => emit('long-wait', details), 10000);
                let finished = false;
                return (outcome: Outcome) => {
                    if (finished) return;
                    finished = true;
                    clearTimeout(timer);
                    emit('finish', { ...details, outcome });
                };
            },
        };
        // pathname distinguishes reused route components; never include it in emitted context.
    }, [pathname, bannerBucket, dqBucket, dqEnabled, draftOrdinal]);
}

export type AutoApplyDiagnostics = ReturnType<typeof useAutoApplyDiagnostics>;

/** Emit changed snapshots, one long-wait per pending interval, and the last committed state on unmount.
 * A null state suppresses diagnostics for redirect-only renders.
 */
export function useAutoApplyDiagnosticState(
    diagnostics: AutoApplyDiagnostics,
    state: DiagnosticState | null,
    draftProctors?: string,
) {
    const serialized = JSON.stringify(state);
    const latest = useRef(state);
    const emit = useMemo(() => diagnostics.capture(draftProctors), [diagnostics, draftProctors]);
    const latestEmit = useRef(emit);
    useEffect(() => {
        const snapshot = JSON.parse(serialized) as DiagnosticState | null;
        latest.current = snapshot;
        latestEmit.current = emit;
        if (snapshot) emit('state', snapshot);
    }, [emit, serialized]);
    useEffect(() => {
        const onPageHide = () => {
            if (latest.current) latestEmit.current('pagehide', latest.current);
        };
        window.addEventListener('pagehide', onPageHide);
        return () => {
            window.removeEventListener('pagehide', onPageHide);
            if (latest.current) latestEmit.current('exit', latest.current);
        };
    }, []);
    useEffect(() => {
        if (!state?.pending) return;
        const timer = setTimeout(() => {
            if (latest.current) emit('long-wait', latest.current);
        }, 10000);
        return () => clearTimeout(timer);
    }, [emit, state?.pending]);
}
